import { NICHT_VORHANDEN, api, ohne404 } from '../api'
import { datum } from '../format'
import { useLaden } from '../hooks'
import { VORGABEGELTUNG } from '../texte'
import Meldung from './Meldung'
import Stammtabelle, { type StammFeld } from './Stammtabelle'
import type { Vorgabe, VorgabeGeltung } from '../typen'

/** „für Artikel 930198, 930270" - wofür eine Vorgabe gilt, in einem Satz. */
function geltungText(v: Pick<Vorgabe, 'geltung' | 'bezug'>): string {
  if (v.geltung === 'allgemein' || !v.bezug) return VORGABEGELTUNG[v.geltung] ?? v.geltung
  return v.geltung === 'artikel' ? `für Artikel ${v.bezug}` : `für ${v.bezug}`
}

const FELDER: StammFeld<Vorgabe>[] = [
  {
    name: 'text',
    titel: 'Vorgabe',
    pflicht: true,
    breit: true,
    platzhalter: 'Bitte immer mit den tatsächlichen monatlichen Produktionszahlen aus 2025/2026 rechnen',
  },
  {
    name: 'geltung',
    titel: 'gilt',
    art: 'auswahl',
    pflicht: true,
    optionen: (Object.keys(VORGABEGELTUNG) as VorgabeGeltung[]).map((g) => ({ wert: g, text: VORGABEGELTUNG[g] })),
    zeigen: geltungText,
  },
  {
    name: 'bezug',
    titel: 'Artikelnummern (mit Komma) oder Lieferant',
    pflicht: true,
    nurFormular: true,
    platzhalter: '930198, 930270 oder Beucke',
    zeigenWenn: (werte) => werte.geltung === 'artikel' || werte.geltung === 'lieferant',
  },
  { name: 'angelegt_von', titel: 'von', nurAnzeige: true },
  { name: 'angelegt_am', titel: 'seit', nurAnzeige: true, zeigen: (v) => datum(v.angelegt_am) },
  { name: 'aktiv', titel: 'an', art: 'schalter', direkt: true },
]

/**
 * Die Vorgaben für den Assistenten (02.11.2026): was jemand ihm dauerhaft gesagt hat („bitte immer mit
 * den tatsächlichen Produktionszahlen rechnen"), bekommt jedes Gespräch mit. Vorher begann jedes
 * Gespräch bei null, und „künftig rechne ich …" galt nur bis zum nächsten. Hier sieht man, was er
 * sich gemerkt hat, kann es ändern, ausschalten, löschen - oder von Hand anlegen.
 */
export default function AssistentVorgaben() {
  const { daten: geholt, fehler, neuLaden } = useLaden<Vorgabe[] | typeof NICHT_VORHANDEN>(() => ohne404(api.vorgaben()), [])
  if (geholt === NICHT_VORHANDEN) return <p className="hinweis">Vorgaben kennt das Backend noch nicht.</p>

  async function speichern(id: number | null, werte: Partial<Vorgabe>) {
    const daten = { text: werte.text ?? '', geltung: werte.geltung ?? 'allgemein', bezug: werte.bezug ?? null }
    if (id === null) {
      const neu = await api.vorgabeAnlegen({ ...daten, bereich: 'verpackung' })
      if (werte.aktiv === false) await api.vorgabeAendern(neu.id, { aktiv: false })
    } else {
      // Der Schalter in der Liste schickt nur `aktiv` - dann auch nur das ändern.
      await api.vorgabeAendern(id, Object.keys(werte).length === 1 && 'aktiv' in werte ? { aktiv: werte.aktiv } : { ...daten, aktiv: werte.aktiv })
    }
    neuLaden()
  }

  async function loeschen(v: Vorgabe) {
    await api.vorgabeLoeschen(v.id)
    neuLaden()
  }

  return (
    <>
      {fehler && <Meldung art="fehler">Die Vorgaben ließen sich nicht laden: {fehler}</Meldung>}
      <Stammtabelle<Vorgabe>
        einzahl="Vorgabe"
        zeilen={geholt}
        felder={FELDER}
        speichern={speichern}
        loeschen={loeschen}
        loeschFrage={(v) => `Vorgabe „${v.text.slice(0, 80)}“ löschen? Ausschalten genügt, wenn sie nur eine Weile nicht gelten soll.`}
        neu={{ geltung: 'allgemein', aktiv: true }}
        leer="Noch keine Vorgaben - der Assistent legt sie an, wenn Sie ihm etwas für künftige Gespräche sagen."
        erklaerung="Jedes Gespräch des Assistenten bekommt die eingeschalteten Vorgaben mit."
        scrollen="formular"
      />
    </>
  )
}
